/** The client and the live-update health, handed down once by the shell so a screen never imports the client (ADR-0027 §2). */
import { createContext, useContext, type ReactNode } from "react";
import type { RouteName, ResponseOf } from "@bsim/contracts";
import { useQueries, useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import type { ApiClient } from "./client";
import { SSE_POLL_MS } from "./sse-health";
import type { ErrorBody } from "@bsim/contracts";
import type { ApiResult, QueryOf, RouteParams } from "./types";

export type StreamHealth = "open" | "reconnecting" | "polling";

interface ApiContextValue { readonly api: ApiClient; readonly health: StreamHealth }

const ApiContext = createContext<ApiContextValue | null>(null);

export function ApiProvider({ api, health, children }: ApiContextValue & { readonly children: ReactNode }) {
  return <ApiContext value={{ api, health }}>{children}</ApiContext>;
}

function useApiContext(): ApiContextValue {
  const ctx = useContext(ApiContext);
  if (ctx === null) throw new Error("ApiProvider is missing");
  return ctx;
}

export const useApi = (): ApiClient => useApiContext().api;

/** A failed call as a thrown value inside react-query only; screens read it through `failureOf`. */
export class ApiFailure extends Error {
  constructor(readonly result: Extract<ApiResult<never>, { ok: false }>) {
    super(result.error.message);
  }
}

/** The api's error from a query's thrown value. Anything else that reached the query (a reply that could not be read, a thrown transport error) is shown as a plain failure, never left to drop the page to its error boundary. */
export function failureOf(error: unknown): ErrorBody["error"] {
  return error instanceof ApiFailure ? error.result.error : { code: "INTERNAL", message: error instanceof Error && error.message !== "" ? error.message : "Something went wrong while loading. Try again.", requestId: "" };
}

/** A failure worth a quiet retry: the connection dropped (status 0), a proxy answered 502/503/504, or a reply that could not be read (the client's own unreadable-reply failure carries no request id). A refusal (4xx) or the api's own 500 is not transient. */
export function isTransientFailure(error: unknown): boolean {
  if (!(error instanceof ApiFailure)) return false;
  const { status, error: body } = error.result;
  return status === 0 || status === 502 || status === 503 || status === 504 || (body.code === "INTERNAL" && body.requestId === "" && (status < 400 || status >= 500));
}

const TRANSIENT_RETRIES = 3;

/** Query options for one GET route; the key is the route name and its params, so a screen and the shell share the cache. */
function routeQuery<N extends RouteName>(api: ApiClient, health: StreamHealth, name: N, params: RouteParams<N> | null, staleTime?: number, query?: QueryOf<N>, round?: number): UseQueryOptions<ResponseOf<N>, ApiFailure, ResponseOf<N>, readonly unknown[]> {
  return {
    queryKey: [name, params, ...(query === undefined ? [] : [query]), ...(round === undefined ? [] : [{ round }])],
    enabled: params !== null,
    ...(staleTime === undefined ? {} : { staleTime }),
    retry: (failureCount: number, error: ApiFailure) => failureCount < TRANSIENT_RETRIES && isTransientFailure(error),
    retryDelay: (failureCount: number) => Math.min(1000 * 2 ** failureCount, 4000),
    refetchInterval: health === "polling" ? SSE_POLL_MS : (false as const),
    queryFn: async (): Promise<ResponseOf<N>> => {
      const result = await api.call(name, params as RouteParams<N>, undefined as never, {}, query);
      if (!result.ok) throw new ApiFailure(result);
      return result.value;
    },
  };
}

/** GET a route through the shared cache. `params` is the query identity; pass `null` to wait (`enabled: false`). */
export function useRoute<N extends RouteName>(name: N, params: RouteParams<N> | null, opts: { readonly staleTime?: number; readonly query?: QueryOf<N>; /** A read that is the bank as of one round is cached under it, so a new round never shows the last one's copy. */ readonly round?: number } = {}): UseQueryResult<ResponseOf<N>, ApiFailure> {
  const { api, health } = useApiContext();
  return useQuery<ResponseOf<N>, ApiFailure>(routeQuery(api, health, name, params, opts.staleTime, opts.query, opts.round));
}

/** The same GET for a list of param sets (e.g. one `roundReport` per released round); `queries[i]` is the query string of `paramsList[i]` (e.g. `publicMarket` through each round). */
export function useRoutes<N extends RouteName>(name: N, paramsList: readonly RouteParams<N>[], queries: readonly (QueryOf<N> | undefined)[] = []): UseQueryResult<ResponseOf<N>, ApiFailure>[] {
  const { api, health } = useApiContext();
  return useQueries({ queries: paramsList.map((params, i) => routeQuery(api, health, name, params, undefined, queries[i])) });
}
